import type {
  AliasHighlight,
  DocumentSubject,
} from 'features/knowledgeDocumentViewer/types/documentSubjects'

/**
 * Flatten subjects → aliases → boxes into drawable highlights, converting the
 * API's `[x0, y0, x1, y1]` into the viewer's left/top/width/height form.
 * References persisted without coordinates arrive as `bbox: null` and are
 * skipped — they ground the subject on the document but aren't drawable.
 * Boxes repeated at the same coordinates (the same occurrence reached through
 * several subjects or aliases) collapse to one highlight, so translucent fills
 * never stack into an opaque block over the text.
 */
export function flattenAliasHighlights(
  subjects: DocumentSubject[]
): AliasHighlight[] {
  const highlights: AliasHighlight[] = []
  const seen = new Set<string>()
  for (const subject of subjects) {
    for (const alias of subject.aliases) {
      for (const box of alias.boxes) {
        if (!box.bbox) continue
        const [x0, y0, x1, y1] = box.bbox
        const key = `${box.page}:${x0.toFixed(5)}:${y0.toFixed(5)}:${x1.toFixed(5)}:${y1.toFixed(5)}`
        if (seen.has(key)) continue
        seen.add(key)
        highlights.push({
          page: box.page,
          bbox: {
            left: x0,
            top: y0,
            width: x1 - x0,
            height: y1 - y0,
          },
          subjectUid: subject.subject_uid,
          aliasNorm: alias.norm,
          label: alias.value,
        })
      }
    }
  }
  return highlights
}
